<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';

import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import ContactMoreActions from './components/ContactMoreActions.vue';
import ContactScopeFilter from './components/ContactScopeFilter.vue';

// Contacts header, 1:1 with the CrmB mockup. Row 1: title + count, search,
// "Nuevo contacto". Row 2: Tipo / Estado / pills, then "Limpiar" and
// "Guardar vista" (or "Editar vista" + delete on a saved segment).
const props = defineProps({
  searchValue: { type: String, default: '' },
  headerTitle: { type: String, required: true },
  activeSort: { type: String, default: 'last_activity_at' },
  activeOrdering: { type: String, default: '' },
  isSegmentsView: { type: Boolean, default: false },
  hasActiveFilters: { type: Boolean, default: false },
  isLabelView: { type: Boolean, default: false },
  isActiveView: { type: Boolean, default: false },
});

const emit = defineEmits([
  'search',
  'update:sort',
  'add',
  'import',
  'export',
  'saveView',
  'deleteSegment',
  'applyAttributeFilters',
  'clearFilters',
]);

const { t, n } = useI18n();
const meta = useMapGetter('contacts/getMeta');
const totalLabel = computed(() =>
  t('CONTACTS_LAYOUT.HEADER.TOTAL_COUNT', {
    count: n(Number(meta.value?.count || 0)),
  })
);

const showViewActions = computed(
  () => !props.isLabelView && !props.isActiveView
);
const showClear = computed(
  () => props.hasActiveFilters && !props.isSegmentsView
);
const saveViewLabel = computed(() =>
  props.isSegmentsView
    ? t('CONTACTS_LAYOUT.HEADER.EDIT_VIEW')
    : t('CONTACTS_LAYOUT.HEADER.SAVE_VIEW')
);
</script>

<template>
  <header class="sticky top-0 z-10 bg-n-solid-2 border-b border-n-weak">
    <div class="flex items-center gap-3 px-5 pt-4">
      <h1 class="m-0 text-lg font-semibold truncate text-n-slate-12">
        {{ headerTitle }}
      </h1>
      <span class="text-[12.5px] text-n-slate-10 truncate">{{ totalLabel }}</span>

      <div class="flex items-center gap-2 ml-auto flex-shrink-0">
        <label
          class="relative flex items-center w-[300px] max-w-[40vw] h-[34px] rounded-[7px] bg-n-alpha-2 text-n-slate-10 focus-within:bg-n-alpha-1 focus-within:ring-1 focus-within:ring-n-brand/50 transition-colors"
        >
          <Icon icon="i-lucide-search" class="absolute ltr:left-3 rtl:right-3 size-[15px]" />
          <input
            :value="searchValue"
            type="search"
            :placeholder="t('CONTACTS_LAYOUT.HEADER.SEARCH_PLACEHOLDER')"
            class="w-full h-full bg-transparent border-0 outline-none reset-base text-[13px] text-n-slate-12 placeholder:text-n-slate-10 ltr:pl-9 rtl:pr-9 ltr:pr-3 rtl:pl-3"
            @input="emit('search', $event.target.value)"
          />
        </label>
        <ContactMoreActions
          :active-sort="activeSort"
          :active-ordering="activeOrdering"
          @import="emit('import')"
          @export="emit('export')"
          @update:sort="emit('update:sort', $event)"
        />
        <Button
          icon="i-lucide-plus"
          size="sm"
          class="h-[34px] rounded-[7px]"
          :label="t('CONTACTS_LAYOUT.HEADER.NEW_CONTACT_BUTTON')"
          @click="emit('add')"
        />
      </div>
    </div>

    <div class="flex items-center gap-2 px-5 pt-[13px] pb-3">
      <ContactScopeFilter
        class="flex-1"
        @apply-attribute-filters="emit('applyAttributeFilters', $event)"
        @clear-filters="emit('clearFilters')"
      />
      <div v-if="showViewActions" class="relative flex items-center flex-shrink-0 gap-2">
        <button
          v-if="showClear"
          type="button"
          class="text-xs font-semibold text-n-blue-text whitespace-nowrap hover:underline"
          @click="emit('clearFilters')"
        >
          {{ t('CONTACTS_LAYOUT.HEADER.SCOPE_FILTER.CLEAR') }}
        </button>
        <button
          type="button"
          class="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-[7px] border border-dashed border-n-slate-7 text-[11.5px] font-medium whitespace-nowrap transition-colors hover:border-n-brand hover:text-n-blue-text"
          :class="hasActiveFilters || isSegmentsView ? 'text-n-slate-11' : 'text-n-slate-10'"
          @click="emit('saveView')"
        >
          <Icon :icon="isSegmentsView ? 'i-lucide-pen-line' : 'i-lucide-bookmark-plus'" size="size-3.5" />
          {{ saveViewLabel }}
        </button>
        <Button
          v-if="isSegmentsView"
          v-tooltip.bottom="t('CONTACTS_LAYOUT.HEADER.DELETE_VIEW')"
          icon="i-lucide-trash"
          color="slate"
          size="sm"
          variant="ghost"
          class="w-7 h-7"
          @click="emit('deleteSegment')"
        />
        <slot name="filter" />
      </div>
    </div>
  </header>
</template>
